// LikesPanel.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from "react-redux";
import { MdClose } from 'react-icons/md';
import { useUser } from '../../../hooks/useUser';
import { usePost } from '../../../hooks/usePost';
import './LikesPanel.scss';
import { useNavigate } from 'react-router-dom';

const USERS_PER_PAGE = 10;

const LikesPanel = ({ isOpen, onClose, postId, likesCount }) => {
    const [page, setPage] = useState(1);
    const currentUserId = useSelector((state) => state.projectInfo.uid);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [displayedUsers, setDisplayedUsers] = useState([]);
    const { handleUserFollowUnfollow } = useUser();
    const navigate = useNavigate();
    const { getLikedUsers } = usePost();
    const containerRef = useRef(null);

    // Use useRef to prevent the loadMoreUsers function from changing
    // on every render
    const stateRef = useRef({
        loading,
        hasMore,
        page,
    });

    // Update the ref whenever these states change
    useEffect(() => {
        stateRef.current = {
            loading,
            hasMore,
            page,
        };
    }, [loading, hasMore, page]);

    const loadMoreUsers = useCallback(async () => {
        // Use stateRef.current to access current state values
        const { loading, hasMore, page } = stateRef.current;

        if (loading || !hasMore) return;

        setLoading(true);
        try {
            // Get the next batch of liked user IDs
            const result = await getLikedUsers(postId, page);
            const totalUsersCount = displayedUsers.length + result.length;
            setDisplayedUsers(prev => [...prev, ...result]);
            setHasMore(totalUsersCount < likesCount);

            // Increment page counter
            setPage(prev => prev + 1);
        } catch (error) {
            console.error('Error loading more users:', error);
        } finally {
            setLoading(false);
        }
    }, [postId, getLikedUsers, displayedUsers]);

    const onFollowUnfollowClick = useCallback(async (userId, currentUserId, isFollowed) => {
        await handleUserFollowUnfollow(userId, currentUserId, isFollowed);
        setDisplayedUsers(prevUsers => prevUsers.map(user =>
            user.user_id === userId ? { ...user, is_following: JSON.stringify(!isFollowed) } : user
        ));
    }, [handleUserFollowUnfollow]);

    // Reset panel and load initial users when opening
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';

            // Reset the panel state when it opens
            setPage(1);
            setHasMore(true);
            setDisplayedUsers([]);
            setLoading(false);

            // Manually update the ref to ensure latest values
            stateRef.current = {
                loading: false,
                hasMore: true,
                page: 1,
            };

            // We need to wait a tick to ensure state is properly reset
            setTimeout(() => {
                loadMoreUsers();
            }, 0);
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.x = '';
        };
    }, [isOpen]); // Don't include loadMoreUsers in the dependency array

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('likes-panel-overlay')) {
            onClose();
        }
    };
    const navigateToProfile = (userId) => {
        navigate(`/community/profiles/${userId || "unknown"}`);
    };

    return (
        <>
            <div className="likes-panel-backdrop fade-in"></div>
            <div className="likes-panel-overlay" onClick={handleOverlayClick}>
                <div className="likes-panel modal-animation">
                    <div className="likes-panel-header">
                        <h2>Likes</h2>
                        <button onClick={onClose} className="close-button-like hover-grow">
                            <MdClose size={24} />
                        </button>
                    </div>
                    <div className="likes-panel-content" ref={containerRef}>
                        {displayedUsers.map((likedUser) => {
                            const isFollowing = JSON.parse(likedUser?.is_following) || false;
                            return (
                                <div key={likedUser.user_id} className="liked-user-item stagger-item">
                                    <img
                                        src={likedUser?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                                        alt={likedUser?.user_name}
                                        className="user-profile-pic hover-grow"
                                        onClick={() => navigateToProfile(likedUser.user_id)}
                                    />
                                    <div className="user-info">
                                        <span className="user-name">{likedUser?.user_name || "Unknown User"}</span>
                                        <span className="user-full-name">{likedUser?.fullName || ""}</span>
                                    </div>
                                    <button
                                        className={`follow-button ${isFollowing ? 'following' : ''} hover-brighten ripple`}
                                        hidden={likedUser.user_id === Number(currentUserId)}
                                        onClick={() => { onFollowUnfollowClick(likedUser.user_id, currentUserId, isFollowing) }}
                                    >
                                        {isFollowing ? "Unfollow" : "Follow"}
                                    </button>
                                </div>
                            );
                        })}
                        {hasMore && !loading && (
                            <div className="load-more-container" onClick={loadMoreUsers}>
                                Load More
                            </div>
                        )}
                        {loading && <div className="loading">Loading...</div>}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LikesPanel;